import { createSlice } from '@reduxjs/toolkit';
import { setFieldPropertyForPage, setFieldPropertyForQuestion, setFieldPropertyForSection } from '../../../components/Helpers/AppDesignerControlSettingsHelpers';
import { handleAddSection, handleRemoveControl, handleAddTemplateSection, handleAddingEmptyQuestions } from '../../../components/Helpers/ApplicationDesignerLayoutActionsHelpers';
import { didTemplateChange, getSelectedControlIdFromAppTemplateControlType, reArrangeGroupsOnAdd } from '../../../components/Helpers/TemplateHelpers';
import { CONSTANTS } from '../../../components/Helpers/Configuration';
import { checkForAssociatedConditionalLogicThenRemove } from '../../../components/Helpers/DeleteControlWithConditionsConfirmationDialogHelpers';
import dragDropReducers from '../reducerHelpers/dragDropReducers';
import set from 'lodash/set';
import get from 'lodash/get';

const templateSliceInitialState = {
    applicationTemplateData: null,
    applicationPages: [],
    applicationSections: [],
    applicationGroups: [],
    applicationQuestions: [],
    applicationMessages: null,
    selectedControl: null,
    theme: null,
    dndHoverStyles: {},
    associatedConditionalLogicItems: [],
    selectedMainMenuTab: CONSTANTS.adminMenuItems.editForm,
    sidebarTabIndex: 1,
    dragDropErrors: []
};
const templateSlice = createSlice({
    name: 'template',
    initialState: templateSliceInitialState,
    reducers: {
        initializeTemplateSliceState: (state) => {
            state = templateSliceInitialState;
            return state;
        },
        setTemplateValues: (state, action) => {
            const payload = action.payload;
            const pages = payload.pages;
            let templateData = payload?.templateData;
            const templateChanged = didTemplateChange(state.applicationTemplateData, templateData);
            if (templateChanged || !state?.selectedControl?.pageId) {
                // Reset selected control if template changes or if nothing is selected
                state.selectedControl = {
                    controlType: CONSTANTS.appTemplateControlTypes.page,
                    pageId: pages.length > 0 ? pages[0].id : null,
                    pageType: pages.length > 0 ? pages[0].type : null,
                };
                // Reset publishWarningShown if template changes
                templateData = { ...templateData, publishWarningShown: false };
            }
            state.originalAppplicationTemplateData = payload;
            state.applicationTemplateData = templateData;
            state.applicationPages = pages;
            state.applicationSections = payload.sections;
            state.applicationGroups = payload.groups;
            state.applicationQuestions = payload.questions;
            state.applicationMessages = payload.messages;
            return state;
        },
        updateApplicationTemplateProperty: (state, action) => {
            const payload = action.payload;
            const updateProperties = payload?.updateProperties;
            if (!updateProperties) {
                return state;
            }
            Object.keys(updateProperties).map((property) => {
                set(state.applicationTemplateData, property, updateProperties[property]);
            });
            return state;
        },
        updateTemplateProperty: (state, action) => {
            const payload = action.payload;
            const updateProperties = payload?.updateProperties;
            if (!updateProperties) {
                return state;
            }
            Object.keys(updateProperties).map((property) => {
                set(state, property, updateProperties[property]);
            });
            return state;
        },
        addSupplementalItem: (state, action) => {
            const supplementalItems = [...state.applicationTemplateData.supplementalItems.items, action.payload];
            state.applicationTemplateData.supplementalItems.items = supplementalItems;
            return state;
        },
        updateSupplementalItem: (state, action) => {
            const {
                index,
                item,
            } = action.payload;
            set(state.applicationTemplateData, `supplementalItems.items.${index}`, item);
            return state;
        },
        removeSupplementalItem: (state, action) => {
            const {
                index,
            } = action.payload;
            const supplementalItems = [...state.applicationTemplateData.supplementalItems.items];
            supplementalItems.splice(index, 1);
            state.applicationTemplateData.supplementalItems.items = supplementalItems;
            return state;
        },
        removeFormAssignmentItem: (state, action) => {
            const {
                currentIndex,
            } = action.payload;
            const applicationTemplateRules = [...state.applicationTemplateData.settings.applicationTemplateRules];
            applicationTemplateRules.splice(currentIndex, 1);
            state.applicationTemplateData.settings.applicationTemplateRules = applicationTemplateRules;
            return state;
        },
        updateApplicationTemplateData: (state, action) => {
            state.applicationTemplateData = action.payload;
            return state;
        },
        updateApplicationPages: (state, action) => {
            state.applicationPages = action.payload;
            return state;
        },
        updateApplicationSections: (state, action) => {
            state.applicationSections = action.payload;
            return state;
        },
        updateApplicationGroups: (state, action) => {
            state.applicationGroups = action.payload;
            return state;
        },
        updateApplicationQuestions: (state, action) => {
            state.applicationQuestions = action.payload;
            return state;
        },
        updateApplicationMessages: (state, action) => {
            state.applicationMessages = action.payload;
            return state;
        },
        updateSelectedControl: (state, action) => {
            if (!state.selectedControl) {
                state.selectedControl = action.payload;
            } else {
                state.selectedControl.pageId = action.payload?.pageId || state.selectedControl.pageId || null;
                state.selectedControl.pageType = action.payload?.pageType || state.selectedControl.pageType || null;
                state.selectedControl.sectionId = action.payload?.sectionId || state.selectedControl.sectionId || null;
                state.selectedControl.groupId = action.payload?.groupId || state.selectedControl.groupId || null;
                state.selectedControl.questionId = action.payload?.questionId || state.selectedControl.questionId || null;
                state.selectedControl.controlType = action.payload?.controlType || state.selectedControl.controlType || null;
            }
            return state;
        },
        setTheme: (state, action) => {
            const themeName = action.payload;
            if (!themeName || themeName === 'none' || themeName === 'None') {
                state.applicationTemplateData.theme = null;
            } else {
                state.applicationTemplateData.theme = themeName;
            }
            return state;
        },
        setErrorsOnPageFlag: (state, action) => {
            const payload = action.payload;
            // if pageId wasn't provided grab it from selectedControl
            const pageId = payload?.pageId || state?.selectedControl?.pageId || null;

            if (!pageId) {
                return state;
            }
            // If even one error on any control on the page, set the page.hasError to true
            state.applicationPages.forEach((page, index) => {
                if (page.id === pageId) {
                    if (payload?.hasError) {
                        state.applicationPages[index].hasError = true;
                        return;
                    }
                    if ((page.errors.requiredError || page.errors.conditionalError)) {
                        state.applicationPages[index].hasError = true;
                        return;
                    }
                    const errSection = state.applicationSections.find(s =>
                        s.pageId === pageId && (s.errors.requiredError || s.errors.conditionalError)
                    );
                    if (errSection) {
                        state.applicationPages[index].hasError = true;
                        return;
                    }
                    const errQuestion = state.applicationQuestions.find(q =>
                        q.pageId === pageId && (q.errors.requiredError || q.errors.conditionalError)
                    );
                    const errPhone = state.applicationQuestions.find(q =>
                        q.pageId === pageId && q.type === CONSTANTS.controlTypes.phone && !q.defaultCountry
                    );
                    if (errQuestion || errPhone) {
                        state.applicationPages[index].hasError = true;
                        return;
                    }
                    else {
                        state.applicationPages[index].hasError = false;
                        return;
                    }
                }
            });
            return state;
        },
        setFieldProperty: (state, action) => {
            const fieldType = action?.payload?.fieldType;
            switch (fieldType) {
                case CONSTANTS.appTemplateControlTypes.page:
                    return setFieldPropertyForPage(state, action);
                case CONSTANTS.appTemplateControlTypes.section:
                    return setFieldPropertyForSection(state, action);
                case CONSTANTS.appTemplateControlTypes.question:
                    return setFieldPropertyForQuestion(state, action);
                default:
                    return state;
            }
        },
        updateAssociatedConditionalLogicItems: (state) => {
            const applicationPages = state.applicationPages;
            const applicationSections = state.applicationSections;
            const applicationQuestions = state.applicationQuestions;
            const id = getSelectedControlIdFromAppTemplateControlType(state.selectedControl);
            const controlType = state.selectedControl?.controlType ?? null;
            state.associatedConditionalLogicItems = checkForAssociatedConditionalLogicThenRemove(applicationPages, applicationSections, applicationQuestions, id, controlType);
            return state;
        },
        removeControl: (state) => {
            const applicationPages = state.applicationPages;
            const applicationSections = state.applicationSections;
            const applicationGroups = state.applicationGroups;
            const applicationQuestions = state.applicationQuestions;
            const selectedControl = state.selectedControl;
            const {
                newPages,
                newSections,
                newGroups,
                newQuestions,
                newSelectedControl
            } = handleRemoveControl(applicationPages, applicationSections, applicationGroups, applicationQuestions, selectedControl);
            state.applicationPages = newPages;
            state.applicationSections = newSections;
            state.applicationGroups = newGroups;
            state.applicationQuestions = newQuestions;
            state.selectedControl = newSelectedControl;
            return state;
        },
        addSection: (state) => {
            const applicationPages = state.applicationPages;
            const applicationSections = state.applicationSections;
            const applicationGroups = state.applicationGroups;
            const applicationQuestions = state.applicationQuestions;
            const selectedControl = state.selectedControl;
            const {
                newSections,
                newGroups,
                newQuestions,
                newSelectedControl
            } = handleAddSection(applicationPages, applicationSections, applicationGroups, applicationQuestions, selectedControl);
            state.applicationSections = newSections;
            state.applicationGroups = newGroups;
            state.applicationQuestions = newQuestions;
            state.selectedControl = newSelectedControl;
            return state;
        },
        addTemplateSection: (state, action) => {
            const applicationPages = state.applicationPages;
            const payload = action?.payload;
            const selectedEmptyQuestion = state.applicationQuestions.find(q => q.id === state.selectedControl.questionId);
            const questionIndex = state.applicationQuestions.findIndex(q => q.id === state.selectedControl.questionId);
            const applicationSections = state.applicationSections;
            const applicationGroups = state.applicationGroups;
            const applicationQuestions = state.applicationQuestions;
            const selectedControl = state.selectedControl;
            let newQuestionsArray = payload?.newQuestions;

            if (newQuestionsArray?.length) {
                const {
                    allGroups,
                    allQuestions,
                    newSelectedControl
                } = handleAddTemplateSection(applicationGroups, applicationQuestions, selectedControl, selectedEmptyQuestion, newQuestionsArray, questionIndex);

                const { allQuestions: newQuestions, allGroups: newGroups } = handleAddingEmptyQuestions(allQuestions, allGroups, applicationSections, applicationPages, newSelectedControl);

                state.applicationGroups = newGroups;
                state.applicationQuestions = newQuestions;
                state.selectedControl = newSelectedControl;
            }

            return state;
        },
        addControl: (state, action) => {
            const payload = action?.payload;
            const selectedEmptyQuestion = state.applicationQuestions.find(q => q.id === state.selectedControl.questionId);
            const questionIndex = state.applicationQuestions.findIndex(q => q.id === state.selectedControl.questionId);
            const applicationSections = state.applicationSections;
            const applicationGroups = state.applicationGroups;
            const applicationQuestions = state.applicationQuestions;
            const selectedControl = state.selectedControl;
            let newQuestion = payload?.newQuestion;
            if (!newQuestion || !selectedEmptyQuestion || questionIndex == null || questionIndex < 0) {
                return state;
            }


            newQuestion = {
                ...selectedEmptyQuestion,
                ...newQuestion
            };
            applicationQuestions[questionIndex] = newQuestion;

            const { newApplicationGroups, newApplicationQuestions } = reArrangeGroupsOnAdd(applicationSections, applicationGroups, applicationQuestions, selectedControl);
            state.applicationGroups = newApplicationGroups;
            state.applicationQuestions = newApplicationQuestions;
            return state;
        },
        setSelectedMainMenuTab: (state, action) => {
            state.selectedMainMenuTab = action.payload;
            return state;
        },
        setSelectedMainMenuTabIndex: (state, action) => {
            state.selectedMainMenuTab = action.payload;
            const { adminMenuItems } = CONSTANTS;
            switch (action.payload) {
                case 0:
                    state.selectedMainMenuTab = adminMenuItems.editForm;
                    break;
                case 1:
                    state.selectedMainMenuTab = adminMenuItems.themeEditor;
                    break;
                case 2:
                    state.selectedMainMenuTab = adminMenuItems.configuration;
                    break;
                case 3:
                    state.selectedMainMenuTab = adminMenuItems.notifications;
                    break;
                case 4:
                    state.selectedMainMenuTab = adminMenuItems.submissions;
                    break;
                default:
                    return 0;
            }
            return state;
        },
        setSidebarTabIndex: (state, action) => {
            state.sidebarTabIndex = action.payload;
            return state;
        },
        addFee: (state, action) => {
            const { newFee } = action.payload;
            const newFees = [...state.applicationTemplateData.settings.paymentInformation.applicationFees];

            newFees.push(newFee);
            state.applicationTemplateData.settings.paymentInformation.applicationFees = newFees;
            return state;
        },
        removeFee: (state, action) => {
            const { feeIndex } = action.payload;
            const newFees = [...state.applicationTemplateData.settings.paymentInformation.applicationFees];
            newFees.splice(feeIndex, 1);
            state.applicationTemplateData.settings.paymentInformation.applicationFees = newFees;
            return state;
        },
        updateFee: (state, action) => {
            const { feeIndex, newFee } = action.payload;

            set(state.applicationTemplateData, `settings.paymentInformation.applicationFees.${feeIndex}`, newFee);
            return state;
        },
        moveFee: (state, action) => {
            const { feeIndex, newIndex } = action.payload;
            const newFees = [...state.applicationTemplateData.settings.paymentInformation.applicationFees];
            if (feeIndex < newIndex) {
                newFees.splice(feeIndex, 2, newFees[newIndex], newFees[feeIndex]);
            }
            else {
                newFees.splice(newIndex, 2, newFees[feeIndex], newFees[newIndex]);
            }
            state.applicationTemplateData.settings.paymentInformation.applicationFees = newFees;
            return state;
        },
        setIndexedQuestion: (state, action) => {
            state.applicationTemplateData.indexedQuestions = action.payload;
            return state;
        },
        ...dragDropReducers
    },
});

export const {
    addControl,
    addFee,
    addSection,
    addTemplateSection,
    addSupplementalItem,
    initializeTemplateSliceState,
    removeControl,
    removeFee,
    moveFee,
    removeFormAssignmentItem,
    removeSupplementalItem,
    setErrorsOnPageFlag,
    setFieldProperty,
    setIndexedQuestion,
    setSelectedMainMenuTab,
    setSelectedMainMenuTabIndex,
    setSidebarTabIndex,
    setTemplateValues,
    setTheme,
    updateApplicationGroups,
    updateApplicationMessages,
    updateApplicationPages,
    updateApplicationQuestions,
    updateApplicationSections,
    updateApplicationTemplateData,
    updateFee,
    updateTemplateProperty,
    updateApplicationTemplateProperty,
    updateAssociatedConditionalLogicItems,
    updateSelectedControl,
    updateSupplementalItem,
    // Drag Drop
    setDndHoverStyles,
    beginMoveTypeQuestionReducer,
    moveTypeQuestionReducer,
    dropTypeQuestionReducer,
    undropTypeQuestionReducer,
    beginMoveTypeSectionReducer,
    moveTypeSectionReducer,
    dropTypeSectionReducer,
    undropTypeSectionReducer,
    beginMoveTypePageReducer,
    moveTypePageReducer,
    dropTypePageReducer,
    undropTypePageReducer,
    clearDragDropErrors
} = templateSlice.actions;

export const selectApplicationTemplate = (state) => state.template;
export const selectApplicationTemplateData = (state) => state.template.applicationTemplateData;
export const selectOriginalApplicationTemplateData = (state) => state.template.originalAppplicationTemplateData;
export const selectApplicationTemplateDataProperty = (state, property) => get(state.template.applicationTemplateData, property, null);
export const selectApplicationPages = (state) => state.template.applicationPages;
export const selectApplicationSections = (state) => state.template.applicationSections;
export const selectApplicationGroups = (state) => state.template.applicationGroups;
export const selectApplicationQuestions = (state) => state.template.applicationQuestions;
export const selectApplicationMessages = (state) => state.template.applicationMessages;
export const selectSelectedControl = (state) => state.template.selectedControl;
export const selectTheme = (state) => state.template.applicationTemplateData.theme;
export const selectDndHoverStyles = (state) => state.template.dndHoverStyles;
export const selectAssociatedConditionalLogicItems = (state) => state.template.associatedConditionalLogicItems;

export const selectCurrentPage = (state) => state.template.applicationPages.find(p => p.id === state.template.selectedControl.pageId);
export const selectCurrentPageId = (state) => state.template.applicationPages.find(p => p.id === state.template.selectedControl.pageId)?.id ?? null;
export const selectSelectedControlPageType = (state) => state.template.selectedControl?.pageType || null;

export const selectNumberOfSectionsForPage = (state, pageId) => state.template.applicationSections.filter(p => p.pageId === pageId)?.length;
export const selectNumberOfGroupsForSection = (state, sectionId) => state.template.applicationGroups.filter(g => g.sectionId === sectionId)?.length;
export const selectNumberOfQuestionsForGroup = (state, groupId) => state.template.applicationQuestions.filter(q => q.groupId === groupId)?.length;
export const selectQuestionsForGroup = (state, groupId) => state.template.applicationQuestions.filter(q => q.groupId === groupId);

export const selectIsImportTemplate = (state) => state.template.applicationTemplateData?.settings?.applicationTemplateType === CONSTANTS.applicationTemplateTypes.import;

export const getSectionByIndexAndPageId = (state, pageId, index) => state.template.applicationSections.filter(s => s.pageId === pageId)[index];
export const getGroupByIndexAndSectionId = (state, sectionId, index) => state.template.applicationGroups.filter(g => g.sectionId === sectionId)[index];
export const getQuestionByIndexAndGroupId = (state, groupId, index) => state.template.applicationQuestions.filter(q => q.groupId === groupId)[index];

export const selectSidebarTabIndex = (state) => state.template.sidebarTabIndex;
export const selectDragDropError = (state) => state.template.dragDropErrors;

export const selectSelectedMainMenuTab = (state) => state.template.selectedMainMenuTab;
export const selectIndexedQuestions = (state) => state.template.applicationTemplateData.indexedQuestions;
export const selectMainMenuTabIndex = state => {
    const { adminMenuItems } = CONSTANTS;
    switch (state.template.selectedMainMenuTab) {
        case adminMenuItems.editForm:
            return 0;
        case adminMenuItems.themeEditor:
            return 1;
        case adminMenuItems.configuration:
            return 2;
        case adminMenuItems.notifications:
            return 3;
        case adminMenuItems.submissions:
            return 4;
        default:
            return 0;
    }
};

export default templateSlice.reducer;
